import { pollingRequest, RootState, store, useAppDispatch } from '@AppState/redux-store'
import { useSelector } from 'react-redux'
import { AnimationBackground } from '@Components/animation/background/background'
import { ANIMATION_PLAY_STATE, AnimationActions } from '@AppState/animation'

import { useEffect, useState } from 'react'
import { AnimationAddOnPanelProps } from '@Components/animation/add-on-panel/animation-add-on-panel'
import { TicketModeController } from '@Components/animation/controls/ticket-mode-animation-controls'

import './styles.scss'
import { fetchTicketResults } from '@AppState/ticket-info/ticket-info-slice'
import { TicketInfo } from '@Config/ticket-form-types'
import { PollingActions } from '@AppState/polling/polling-slice'
import { ScoredDrawResultComplete } from '@API/game-results/types.ts'
import { AwaitingNextDrawModalProps } from '@Components/animation/awaiting-next-draw/awaiting-next-draw'
import { AnimationPayoutModal } from '@Components/animation/payout-modal/payout-modal'
import { WatchAnimation } from './watch-animation'
import { AwaitingNextDrawUtils } from '@Components/animation/awaiting-next-draw/utils'
import { AnimationAddOnPanelUtils } from '@Components/animation/add-on-panel/utils'
import { useTimeout } from '@Hooks/use-timeout'
import { fetchLatestDraw } from '@AppState/latest-draw'

const CLASS_NAME = 'gby-k2g-watch-animation'
const POLLING_ID = 'fetchTicketResults'
const LATEST_DRAW_POLLING_ID = 'fetchLatestDraw'

type TicketWatchModeProps = {
  drawIndex: number
  onDrawingIndexChange: (index: number) => void
  onNewTicketClick: () => void
}

export const TicketWatchMode = (props: TicketWatchModeProps) => {
  const latestDraw = useSelector((state: RootState) => state.latestDraw.latestDraw)
  const addOnConfig = useSelector((state: RootState) => state.hostConfig.config?.addOns)
  const { drawIndex, onDrawingIndexChange } = props
  const { activeTicket, activeTicketResults } = useSelector((state: RootState) => state.ticketMode)
  const animationState = useSelector((state: RootState) => state.animation)
  const addOnConfigs = useSelector((state: RootState) => state.hostConfig.config?.addOns ?? [])
  const [payoutModalElement, setPayoutModalElement] = useState<JSX.Element | null>(null)
  const [awaitingNextDrawProps, setAwaitingNextDrawProps] =
    useState<AwaitingNextDrawModalProps | null>(null)
  const pollingState = useSelector((state: RootState) => state.polling)
  const dispatch = useAppDispatch()

  const { clear: clearPayoutModal, setTimeoutDelay: setPayoutModalDelay } = useTimeout(() => {
    if (!activeTicketResults) return
    setPayoutModalElement(
      <AnimationPayoutModal
        totalWinnings={activeTicketResults.totalWinningsInCents}
        numberOfDraws={activeTicketResults.scoredDrawResults.length}
        onButtonClick={props.onNewTicketClick}
        onClose={() => {
          clearPayoutModal()
          setPayoutModalElement(null)
        }}
      />,
    )
  }, null)

  const [addOnPanelProps, setAddOnPanelProps] = useState<AnimationAddOnPanelProps>({
    display: false,
    multiplierAddOn: undefined,
    miniGameAddOn: undefined,
  })

  const hasTicketResults = activeTicketResults
    ? activeTicketResults.scoredDrawResults[drawIndex] !== undefined
    : false

  const animationReady =
    animationState.playState !== ANIMATION_PLAY_STATE.GAME_LOADING && hasTicketResults

  useEffect(() => {
    const defaultProps: AnimationAddOnPanelProps = {
      display: animationState.showAddonPanel,
      multiplierAddOn: undefined,
      miniGameAddOn: undefined,
    }

    if (!activeTicketResults || !activeTicket) {
      setAddOnPanelProps(defaultProps)
      return
    }

    const result = activeTicketResults.scoredDrawResults[drawIndex]

    if (!result || !result.drawingComplete) {
      setAddOnPanelProps(defaultProps)
      return
    }

    setAddOnPanelProps(
      AnimationAddOnPanelUtils.mapCompletedTicketToProps(
        activeTicket,
        result,
        addOnConfigs,
        animationState.displayNumberIndex,
        animationState.showAddonPanel,
      ),
    )
  }, [animationState, activeTicketResults, activeTicket, addOnConfigs, drawIndex])

  useEffect(() => {
    if (
      activeTicket &&
      activeTicketResults &&
      !activeTicketResults.ticketComplete &&
      !pollingState?.intervalMap[POLLING_ID]
    ) {
      dispatch(pollingRequest<TicketInfo>(fetchTicketResults(activeTicket), POLLING_ID))
      dispatch(pollingRequest(fetchLatestDraw(), LATEST_DRAW_POLLING_ID))
    } else if (activeTicketResults?.ticketComplete) {
      dispatch(PollingActions.clearPollingInterval(POLLING_ID))
      dispatch(PollingActions.clearPollingInterval(LATEST_DRAW_POLLING_ID))
    }
  }, [activeTicket, activeTicketResults, dispatch, pollingState.intervalMap])

  useEffect(() => {
    if (
      activeTicket &&
      activeTicketResults &&
      activeTicketResults.ticketComplete &&
      animationState.playState === ANIMATION_PLAY_STATE.FINISHED &&
      drawIndex + 1 === activeTicket.numberOfDraws
    ) {
      setPayoutModalDelay(5000)
    } else if (animationState.playState !== ANIMATION_PLAY_STATE.FINISHED) {
      clearPayoutModal()
    }
  }, [
    activeTicket,
    drawIndex,
    activeTicketResults,
    props.onNewTicketClick,
    animationState.playState,
    clearPayoutModal,
    setPayoutModalDelay,
  ])

  useEffect(() => {
    return () => {
      dispatch(PollingActions.clearPollingInterval(POLLING_ID))
      dispatch(PollingActions.clearPollingInterval(LATEST_DRAW_POLLING_ID))
    }
  }, [dispatch])

  useEffect(() => {
    return () => {
      clearPayoutModal()
    }
  }, [clearPayoutModal])

  useEffect(() => {
    if (
      latestDraw &&
      activeTicketResults &&
      activeTicket &&
      addOnConfig &&
      !activeTicketResults.scoredDrawResults[drawIndex].drawingComplete
    ) {
      const lastCompleteResult = activeTicketResults.scoredDrawResults
        .filter((res) => res.drawingComplete)
        .pop()

      if (lastCompleteResult) {
        setAwaitingNextDrawProps(
          AwaitingNextDrawUtils.mapCompletedTicketToProps(
            activeTicket,
            lastCompleteResult,
            addOnConfig,
          ),
        )
      } else {
        setAwaitingNextDrawProps({
          drawingNumber: activeTicketResults.scoredDrawResults[drawIndex].drawingNumber,
          latestDrawNumber: latestDraw,
          matchDetails: [],
          addOnDetails: [],
          multiplierAddOnResult: undefined,
          playerSelectedNumbers: activeTicket.numberSelections,
        })
      }
    } else {
      setAwaitingNextDrawProps(null)
    }
  }, [latestDraw, activeTicketResults, activeTicket, addOnConfig, drawIndex])

  if (!activeTicket) {
    return <AnimationBackground />
  }

  return (
    <div className={CLASS_NAME}>
      <AnimationBackground />

      <WatchAnimation
        animationReady={animationReady}
        addOnPanelProps={addOnPanelProps}
        awaitingNextDrawProps={awaitingNextDrawProps}
        controls={
          <TicketModeController
            animationReady={animationReady}
            animationResultIndex={animationState.displayNumberIndex}
            currentDrawingIndex={drawIndex}
            latestDraw={latestDraw}
            ticketInfo={activeTicket}
            ticketResult={
              activeTicketResults ?? {
                scoredDrawResults: [],
                ticketComplete: false,
                totalWinningsInCents: 0,
              }
            }
            onDrawingIndexChange={(index) => {
              onDrawingIndexChange(index)
              store.dispatch( AnimationActions.resetAnimation() )
              
            }}
          />
        }
        adaDescription={
          activeTicketResults?.scoredDrawResults &&
          activeTicketResults.scoredDrawResults[props.drawIndex]?.drawingComplete
            ? `Keno Drawing #${activeTicketResults.scoredDrawResults[props.drawIndex].drawingNumber} results animated below: ${(activeTicketResults.scoredDrawResults[props.drawIndex] as ScoredDrawResultComplete).winningNumbers.map((n) => n.numberSelection)}`
            : null
        }
        title={`Watch Ticket`}
      />

      {payoutModalElement}
    </div>
  )
}
